import React, { useCallback, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { registerUser } from '../../store/slices/authSlice';
import { openModal, closeModal } from '../../store/slices/uiSlice';
import toast from 'react-hot-toast';
import TextCaptcha from './TextCaptcha';
import { AIRLINER_FREE_EMAIL_MESSAGE, isAirlinerFreeEmail } from '../../constants/validation';
import useEmailFieldValidation from '../../hooks/useEmailFieldValidation';
import { getEmailValidationError, getEmailValidationInputClass } from '../../utils/emailValidation';
import EmailFieldFeedback from '../EmailInput/EmailFieldFeedback';

const SignUpForm = ({ onClose }) => {
  const dispatch = useAppDispatch();
  const { loading } = useAppSelector((state) => state.auth);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    user_type: '',
    account_type: '',
  });

  const [errors, setErrors] = useState({});
  const [showAccountType, setShowAccountType] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);

  const airlinerEmailValidator = useCallback(
    (email) =>
      formData.user_type === 'Airliner' && isAirlinerFreeEmail(email)
        ? AIRLINER_FREE_EMAIL_MESSAGE
        : null,
    [formData.user_type]
  );

  const {
    emailTouched,
    emailError,
    onEmailChange,
    onEmailBlur,
    validateEmailForSubmit,
  } = useEmailFieldValidation({
    requiredMessage: 'Please enter an email address',
    extraValidator: airlinerEmailValidator,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Show account type selection for JetSetter
    if (name === 'user_type') {
      setShowAccountType(value === 'JetSetter');
      if (value !== 'JetSetter') {
        setFormData(prev => ({ ...prev, account_type: 'Personal' }));
      }
    }

    if (name === 'email') {
      onEmailChange(value);
    }

    if (name === 'user_type' && formData.email.trim()) {
      onEmailChange(formData.email);
    }

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.first_name.trim()) {
      newErrors.first_name = 'Please enter first name';
    } else if (formData.first_name.trim().length < 3) {
      newErrors.first_name = 'First name must be at least 3 characters';
    } else if (!/^[a-zA-Z_ ]*$/.test(formData.first_name)) {
      newErrors.first_name = 'Please enter only letters';
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Please enter last name';
    } else if (formData.last_name.trim().length < 3) {
      newErrors.last_name = 'Last name must be at least 3 characters';
    } else if (!/^[a-zA-Z_ ]*$/.test(formData.last_name)) {
      newErrors.last_name = 'Please enter only letters';
    }

    const emailValidationMessage = getEmailValidationError(formData.email, {
      requiredMessage: 'Please enter an email address',
      extraValidator: airlinerEmailValidator,
    });
    validateEmailForSubmit(formData.email);
    if (emailValidationMessage) {
      newErrors.email = emailValidationMessage;
    }

    if (!formData.user_type) {
      newErrors.user_type = 'Please select a user type';
    }

    if (formData.user_type === 'JetSetter' && !formData.account_type) {
      newErrors.account_type = 'Please select an account type';
    }

    if (!captchaToken) {
      newErrors.captcha = 'Please complete the CAPTCHA verification';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const result = await dispatch(registerUser({
        ...formData,
        captchaToken: captchaToken
      })).unwrap();

      // Reset CAPTCHA after successful registration
      setCaptchaToken(null);

      toast.success(result.message || 'Registration successful! Please check your email for the password setup link.');
      onClose();
    } catch (error) {
      // Reset CAPTCHA on error
      setCaptchaToken(null);

      toast.error(error || 'Registration failed. Please try again.');
    }
  };

  const handleSwitchToLogin = () => {
    dispatch(closeModal('signup'));
    dispatch(openModal('login'));
  };

  return (
    <div className="max-w-md mx-auto p-6">
      <h2 className="text-2xl font-bold text-jet-gray-900 mb-2 text-center">Sign Up</h2>

      <p className="text-jet-gray-600 text-sm mb-6 text-center leading-relaxed">
        Sign Up to create your account and access your personalized profile and your reservations.
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* First Name */}
        <div className="form-group">
          <input
            type="text"
            name="first_name"
            placeholder="FIRST NAME"
            value={formData.first_name}
            onChange={handleChange}
            className="w-full px-4 py-3 border border-jet-gray-300 rounded-md text-sm bg-white text-jet-gray-800 placeholder:text-jet-gray-400 transition-colors duration-300 focus:outline-none focus:border-jet-yellow focus:shadow-[0_0_0_2px_rgba(255,215,0,0.2)] uppercase"
          />
          {errors.first_name && (
            <span className="text-red-500 text-sm mt-1 block">{errors.first_name}</span>
          )}
        </div>

        {/* Last Name */}
        <div className="form-group">
          <input
            type="text"
            name="last_name"
            placeholder="LAST NAME"
            value={formData.last_name}
            onChange={handleChange}
            className="w-full px-4 py-3 border border-jet-gray-300 rounded-md text-sm bg-white text-jet-gray-800 placeholder:text-jet-gray-400 transition-colors duration-300 focus:outline-none focus:border-jet-yellow focus:shadow-[0_0_0_2px_rgba(255,215,0,0.2)] uppercase"
          />
          {errors.last_name && (
            <span className="text-red-500 text-sm mt-1 block">{errors.last_name}</span>
          )}
        </div>

        {/* User Type */}
        <div className="form-group">
          <select
            name="user_type"
            value={formData.user_type}
            onChange={handleChange}
            className="w-full px-4 py-3 border border-jet-gray-300 rounded-md text-sm bg-white text-jet-gray-800 transition-colors duration-300 focus:outline-none focus:border-jet-yellow focus:shadow-[0_0_0_2px_rgba(255,215,0,0.2)]"
          >
            <option value="">Choose User Type</option>
            <option value="Airliner">Airliner</option>
            <option value="JetSetter">JetSetter</option>
          </select>
          {errors.user_type && (
            <span className="text-red-500 text-sm mt-1 block">{errors.user_type}</span>
          )}
        </div>

        {/* Account Type (only for JetSetter) */}
        {showAccountType && (
          <div className="form-group">
            <select
              name="account_type"
              value={formData.account_type}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-jet-gray-300 rounded-md text-sm bg-white text-jet-gray-800 transition-colors duration-300 focus:outline-none focus:border-jet-yellow focus:shadow-[0_0_0_2px_rgba(255,215,0,0.2)]"
            >
              <option value="">Choose Account Type</option>
              <option value="Personal">Personal</option>
              <option value="Business">Business</option>
            </select>
            {errors.account_type && (
              <span className="text-red-500 text-sm mt-1 block">{errors.account_type}</span>
            )}
          </div>
        )}

        {/* Email */}
        <div className="form-group">
          <input
            type="email"
            name="email"
            placeholder="EMAIL ADDRESS"
            value={formData.email}
            onChange={handleChange}
            onBlur={(e) => onEmailBlur(e.target.value)}
            style={{ textTransform: 'uppercase' }}
            className={getEmailValidationInputClass(
              'w-full px-4 py-3 border rounded-md text-sm bg-white text-jet-gray-800 placeholder:text-jet-gray-400 transition-colors duration-300 focus:outline-none focus:shadow-[0_0_0_2px_rgba(255,215,0,0.2)] uppercase',
              {
                touched: emailTouched,
                error: emailError,
                value: formData.email,
                defaultBorderClass: 'border-jet-gray-300 focus:border-jet-yellow',
              }
            )}
          />
          <EmailFieldFeedback
            touched={emailTouched}
            error={emailError || errors.email}
            value={formData.email}
          />
        </div>

        {/* Text CAPTCHA */}
        <div className="form-group">
          <TextCaptcha
            onChange={(token) => {
              setCaptchaToken(token);
              if (errors.captcha) {
                setErrors(prev => ({ ...prev, captcha: '' }));
              }
            }}
            onExpired={() => setCaptchaToken(null)}
          />
          {errors.captcha && <p className="text-red-500 text-sm mt-1">{errors.captcha}</p>}
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-jet-yellow text-jet-gray-900 border-0 py-3 px-6 rounded-md text-sm font-semibold cursor-pointer transition-all duration-300 hover:bg-jet-yellow-hover hover:-translate-y-0.5 focus:outline-none focus:shadow-[0_0_0_2px_rgba(255,215,0,0.4)] mt-6 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:hover:bg-jet-yellow"
        >
          {loading ? 'CREATING ACCOUNT...' : 'SUBMIT'}
        </button>

        <div className="mt-4 text-center">
          <p className="text-sm text-jet-gray-600">
            Already have an account?{' '}
            <button
              type="button"
              onClick={handleSwitchToLogin}
              className="text-jet-yellow font-semibold hover:underline"
            >
              Login
            </button>
          </p>
        </div>
      </form>
    </div>
  );
};

export default SignUpForm;